import { Component, input, computed } from '@angular/core';
import { MonthDayComponent, type MonthDayStatus } from '../month-day/month-day.component';

export interface MonthDayData {
  day: number;
  reservations?: number;
  occupancy?: number;
  status?: MonthDayStatus;
  muted?: boolean;
  today?: boolean;
}

@Component({
  selector: 'lib-month-calendar',
  imports: [MonthDayComponent],
  host: { 'style': 'display: block' },
  template: `
    <div class="msa-month-calendar">
      <div class="msa-month-calendar__weekdays">
        @for (w of weekdays(); track w) {
          <span>{{ w }}</span>
        }
      </div>
      <div class="msa-month-calendar__grid">
        @for (d of days(); track $index) {
          <lib-month-day
            [day]="d.day"
            [reservations]="d.reservations ?? 0"
            [occupancy]="d.occupancy ?? 0"
            [status]="d.status ?? 'confirmada'"
            [muted]="d.muted ?? false"
            [today]="d.today ?? false"
          />
        }
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .msa-month-calendar {
      border: 1px solid #e5e7eb;
      border-radius: 14px;
      overflow: hidden;
      background: #fff;
    }
    .msa-month-calendar__weekdays,
    .msa-month-calendar__grid {
      display: grid;
      grid-template-columns: repeat(7, minmax(96px, 1fr));
    }
    .msa-month-calendar__weekdays span {
      padding: 10px 12px;
      background: #f8fafc;
      color: #6b7280;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }
  `]
})
export class MonthCalendarComponent {
  /** 0-indexed month (0 = January) */
  readonly month = input.required<number>();
  /** Full year (e.g. 2026) */
  readonly year = input.required<number>();
  /** Pre-computed day data keyed by day number (1-31). If provided, uses this. */
  readonly dayData = input<Record<number, Partial<MonthDayData>>>({});

  readonly weekdays = input(['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']);

  readonly days = computed<MonthDayData[]>(() => {
    const m = this.month();
    const y = this.year();
    const data = this.dayData();
    const today = new Date();

    const firstDay = new Date(y, m, 1);
    const lastDay = new Date(y, m + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Monday=0 ... Sunday=6
    let startDow = firstDay.getDay() - 1;
    if (startDow < 0) startDow = 6;

    const result: MonthDayData[] = [];

    // Leading muted days (prev month)
    const prevMonthLast = new Date(y, m, 0).getDate();
    for (let i = startDow - 1; i >= 0; i--) {
      result.push({ day: prevMonthLast - i, muted: true });
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      const isToday = d === today.getDate() && m === today.getMonth() && y === today.getFullYear();
      const extra = data[d] ?? {};
      result.push({
        day: d,
        reservations: extra.reservations ?? 0,
        occupancy: extra.occupancy ?? 0,
        status: extra.status ?? 'confirmada',
        muted: false,
        today: isToday,
      });
    }

    // Trailing muted days (next month) to fill grid to multiple of 7
    const remaining = result.length % 7;
    if (remaining !== 0) {
      const trail = 7 - remaining;
      for (let i = 1; i <= trail; i++) {
        result.push({ day: i, muted: true });
      }
    }

    return result;
  });
}
